import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { DeviceTag } from '../types';

/**
 * FS-501 — device tags.
 *
 * No cache: the tag vocabulary is small and every consumer wants it fresh
 * (a tag created on one device must appear in the next device's typeahead).
 */

// Mounted under /devices, not at /tags: CloudFront routes this API by explicit
// path pattern and `/devices*` already reaches it (aws-infra main.tf
// DevicesApi.path_patterns). A top-level /tags would 404 at the edge.
const tagsUrl = () => `${config.devicesApiUrl}/devices/tags`;

/**
 * The server refused to create a tag because the operator has not confirmed
 * it yet. `similar` carries existing tags within a small edit distance, so the
 * confirmation step can name the tag they probably meant.
 *
 * This is thrown, not swallowed: the confirm gate is enforced server-side, so
 * the UI must handle the refusal rather than assume its own gate ran.
 */
export class TagNeedsConfirmation extends Error {
  constructor(
    // NOT `name`: Error already owns that property, so a parameter property
    // called `name` is immediately clobbered by the `this.name` assignment
    // below and the confirmation panel would offer to create a tag called
    // "TagNeedsConfirmation".
    public readonly tagName: string,
    public readonly similar: string[],
  ) {
    super(`Tag "${tagName}" does not exist yet`);
    this.name = 'TagNeedsConfirmation';
  }
}

/**
 * The server refused to create a tag that is one typo from an existing one.
 * `similar` is the server's own list, computed against the live vocabulary —
 * the client's cached copy is only ever used for instant feedback, never as
 * the thing that decides whether the warning was required.
 */
export class TagTooSimilar extends Error {
  constructor(
    public readonly tagName: string,
    public readonly similar: string[],
  ) {
    super(`Tag "${tagName}" is very similar to an existing tag`);
    this.name = 'TagTooSimilar';
  }
}

/** Narrow an axios failure to one of the two 409 confirm gates, or rethrow. */
function rethrowTagError(err: any, requestedName: string): never {
  const data = err?.response?.data;
  if (err?.response?.status === 409) {
    if (data?.code === 'tag_create_unconfirmed') {
      throw new TagNeedsConfirmation(data.name ?? requestedName, data.similar ?? []);
    }
    if (data?.code === 'tag_similar_unconfirmed') {
      throw new TagTooSimilar(data.name ?? requestedName, data.similar ?? []);
    }
  }
  throw err;
}

/** The tag is still on devices; the server wants an explicit force. */
export class TagStillInUse extends Error {
  constructor(
    public readonly tagName: string,
    /** Authoritative fleet-wide count from the server, not a scoped one. */
    public readonly deviceCount: number,
  ) {
    super(`Tag "${tagName}" is still on ${deviceCount} device(s)`);
    this.name = 'TagStillInUse';
  }
}

export async function fetchTagVocabulary(): Promise<DeviceTag[]> {
  // /list, not the bare prefix: DeviceController's /devices/:id matches any
  // two-segment path and would swallow /devices/tags, parsing "tags" as a
  // device id.
  const res = await axiosInstance.get<DeviceTag[]>(`${tagsUrl()}/list`);
  return res.data;
}

export async function fetchDeviceTags(deviceId: number): Promise<DeviceTag[]> {
  const res = await axiosInstance.get<DeviceTag[]>(`${tagsUrl()}/device/${deviceId}`);
  return res.data;
}

/**
 * Attach `name` to a device. `createNew` is the confirmation for minting a
 * name the vocabulary has not seen; attaching an existing tag never needs it.
 */
export async function attachTag(
  deviceId: number,
  name: string,
  opts: { createNew?: boolean; confirmSimilar?: boolean } = {},
): Promise<DeviceTag> {
  try {
    const res = await axiosInstance.post<DeviceTag>(`${tagsUrl()}/device/${deviceId}`, {
      name,
      create_new: opts.createNew === true,
      confirm_similar: opts.confirmSimilar === true,
    });
    return res.data;
  } catch (err) {
    return rethrowTagError(err, name);
  }
}

export async function detachTag(deviceId: number, tagId: number): Promise<void> {
  await axiosInstance.delete(`${tagsUrl()}/device/${deviceId}/${tagId}`);
}

export async function renameTag(tagId: number, name: string): Promise<DeviceTag> {
  const res = await axiosInstance.put<DeviceTag>(`${tagsUrl()}/${tagId}`, { name });
  return res.data;
}

export async function mergeTags(
  tagId: number,
  intoTagId: number,
): Promise<{ merged: string; into: string; devices_moved: number }> {
  const res = await axiosInstance.post(`${tagsUrl()}/${tagId}/merge`, { into_tag_id: intoTagId });
  return res.data;
}

/**
 * Delete a tag. Called WITHOUT force first: the server answers with the
 * authoritative fleet-wide assignment count, which is the number the operator
 * must see before confirming. The count in the vocabulary listing is narrowed
 * to the caller's entity scope and can read zero for a tag that is on devices
 * they cannot see, so it must never be what a destructive confirmation shows.
 */
export async function deleteTag(tagId: number, force = false): Promise<void> {
  try {
    await axiosInstance.delete(`${tagsUrl()}/${tagId}${force ? '?force=true' : ''}`);
  } catch (err: any) {
    const data = err?.response?.data;
    if (err?.response?.status === 409 && data?.code === 'tag_in_use') {
      throw new TagStillInUse(data.name ?? '', Number(data.device_count ?? 0));
    }
    throw err;
  }
}

/** The whole tag vocabulary with in-scope device counts. */
export function useTagVocabulary(): {
  tags: DeviceTag[];
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [tags, setTags] = useState<DeviceTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTagVocabulary()
      .then((data) => {
        if (!cancelled) setTags(data);
      })
      .catch((err) => {
        if (cancelled) return;
        // A reader without read_devices simply has no vocabulary to show;
        // that is not an error worth putting on screen.
        if (err?.response?.status === 403) {
          setTags([]);
          return;
        }
        setError(err?.message || 'Failed to load tags');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  return { tags, loading, error, reload };
}

/** The tags on one device, with attach/detach that keep local state in step. */
export function useDeviceTags(deviceId: number | null | undefined): {
  tags: DeviceTag[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  setTags: (tags: DeviceTag[]) => void;
} {
  const [tags, setTags] = useState<DeviceTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (deviceId == null) {
      setTags([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDeviceTags(deviceId)
      .then((data) => {
        if (!cancelled) setTags(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setTags([]);
          return;
        }
        setError(err?.message || 'Failed to load device tags');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, nonce]);

  return { tags, loading, error, reload, setTags };
}
